import { Body, IconButton, Tooltip } from '@assembly-js/design-system';
import { useTheme } from '@mui/material/styles';
import {
  SendButtonProps,
  useMessageComposer,
  useMessageComposerHasSendableData,
} from 'stream-chat-react';

import { MAX_CHARACTERS_LIMIT } from 'src/constants/messageConsts';
import { AssistantUserID } from 'src/store/clients/types';

interface CustomSendButtonProps extends SendButtonProps {
  aiAssistantStatus?: {
    status: string;
    metadata?: {
      tool?: string;
      query?: string;
    };
    messageId?: string;
  } | null;
}

// created while referencing the default SendButton component
// https://github.com/GetStream/stream-chat-react/blob/master/src/components/MessageInput/SendButton.tsx
export const CustomSendButton = ({
  sendMessage,
  aiAssistantStatus,
  ...props
}: CustomSendButtonProps) => {
  const theme = useTheme();
  const accentColor = theme.palette.primary.main;

  const hasSendableData = useMessageComposerHasSendableData();

  // Get message composer to check mentions
  const { textComposer } = useMessageComposer();

  // Check if assistant is mentioned
  // We need to check both the mentionedUsers array AND if @Assembly is in the text
  // because mentionedUsers doesn't always clear when the mention is deleted
  const hasAssistantInMentions =
    textComposer.mentionedUsers?.some((user) => user.id === AssistantUserID) ||
    false;

  // Check if @Assembly is actually in the text
  const hasAssistantInText = textComposer.text.includes('@Assembly');

  // Only consider assistant mentioned if both conditions are true
  const isAssistantMentioned = hasAssistantInMentions && hasAssistantInText;

  // Assistant is busy if it has an active status (not null, error, or canceled)
  const isAssistantBusy = !!(
    aiAssistantStatus &&
    aiAssistantStatus.status !== 'error' &&
    aiAssistantStatus.status !== 'canceled'
  );

  // Check if message exceeds character limit
  const exceedsCharacterLimit = textComposer.text.length > MAX_CHARACTERS_LIMIT;

  // Disable if no sendable data OR (assistant mentioned AND busy) OR exceeds character limit
  const isDisabled =
    !hasSendableData ||
    (isAssistantMentioned && isAssistantBusy) ||
    exceedsCharacterLimit;
  const showTooltip =
    hasSendableData && isAssistantMentioned && isAssistantBusy;

  const button = (
    <IconButton
      className="p-[5px] rounded-md disabled:border-transparent"
      icon="SendFilled"
      aria-label="send"
      disabled={isDisabled}
      onClick={sendMessage}
      style={{
        backgroundColor: !isDisabled ? accentColor : undefined,
        borderColor: !isDisabled ? accentColor : undefined,
      }}
      {...props}
    />
  );

  // Wrap in tooltip when assistant is busy and mentioned
  if (showTooltip) {
    return (
      <Tooltip
        content={
          <Body size="xs">
            The Assembly Assistant is currently busy, please wait or cancel.
          </Body>
        }
        position="top"
      >
        <span>{button}</span>
      </Tooltip>
    );
  }

  return button;
};
